import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/Button";
import { Input, Label, Textarea } from "@/components/ui/Input";
import { ResponsiveTable } from "@/components/ui/ResponsiveTable";
import { cn } from "@/lib/cn";
import {
  useAiActions,
  useAiTab,
  useAllBriefs,
  useAudit,
  useCatalog,
  useDigest,
  useDigestActions,
  useKnownData,
  useKnownDataActions,
  useUsage,
  useUserActions,
  type CanaryResult,
} from "@/lib/admin";
import { fmtDate } from "@/lib/briefs";

type Tab = "users" | "briefs" | "ai" | "known" | "digest" | "audit";

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "users", label: "Users" },
  { key: "briefs", label: "All briefs" },
  { key: "ai", label: "AI" },
  { key: "known", label: "Known data" },
  { key: "digest", label: "Digest" },
  { key: "audit", label: "Audit log" },
];

const money = (n: number) => `$${n.toFixed(n < 1 ? 3 : 2)}`;
const pct = (n: number | null) => (n === null ? "—" : `${Math.round(n * 100)}%`);
const ms = (n: number | null) => (n === null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${n}ms`);

export function Admin() {
  // `?tab=digest` is where the digest email's footer link lands.
  const [params] = useSearchParams();
  const initial = TABS.find((t) => t.key === params.get("tab"))?.key ?? "users";
  const [tab, setTab] = useState<Tab>(initial);
  return (
    <main className="mx-auto max-w-6xl px-4 py-7">
      <h1 className="text-xl font-semibold tracking-tight">Admin</h1>
      <p className="text-muted-foreground mt-1 mb-4 text-sm">Superadmin only. Reads of other people's drafts and transcripts are audited.</p>
      <div className="mb-5 flex flex-wrap gap-1" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={cn("rounded-md px-3 py-1.5 text-sm", tab === t.key ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground")}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "users" ? <Users /> : tab === "briefs" ? <AllBriefs /> : tab === "ai" ? <Ai /> : tab === "known" ? <KnownData /> : tab === "digest" ? <Digest /> : <Audit />}
    </main>
  );
}

function Users() {
  const users = useUsage();
  const { setRole, setDeactivated } = useUserActions();
  if (users.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (users.isError) return <p className="text-destructive text-sm">Could not load users.</p>;
  return (
    <ResponsiveTable
      rows={users.data}
      rowKey={(u) => u.id}
      rowClassName={(u) => u.deactivated && "opacity-60"}
      columns={[
        {
          key: "person",
          label: "Person",
          primary: true,
          cell: (u) => (
            <>
              <span className="block font-medium">{u.name ?? u.email}</span>
              {u.name ? <span className="text-muted-foreground block text-xs font-normal">{u.email}</span> : null}
            </>
          ),
        },
        {
          key: "role",
          label: "Role",
          cell: (u) =>
            u.role === "superadmin" ? (
              <span className="text-xs">superadmin</span>
            ) : (
              <select
                aria-label={`Role for ${u.email}`}
                className="bg-card border-input rounded border px-1.5 py-1 text-xs"
                value={u.role ?? "employee"}
                disabled={setRole.isPending}
                onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as "admin" | "employee" })}
              >
                <option value="employee">employee</option>
                <option value="admin">admin</option>
              </select>
            ),
        },
        {
          key: "briefs",
          label: "Briefs (draft / sent / archived)",
          align: "right",
          className: "tabular-nums",
          cell: (u) => (
            <>
              {u.briefs.drafts} / {u.briefs.submitted} / {u.briefs.archived}
            </>
          ),
        },
        { key: "turns", label: "AI turns", align: "right", className: "tabular-nums", cell: (u) => u.ai.turns },
        {
          key: "tokens",
          label: "Tokens in / out",
          align: "right",
          className: "tabular-nums",
          cell: (u) => (
            <>
              {u.ai.inputTokens.toLocaleString()} / {u.ai.outputTokens.toLocaleString()}
            </>
          ),
        },
        { key: "cost", label: "Est. cost", align: "right", className: "tabular-nums", cell: (u) => money(u.ai.costUsd) },
        { key: "seen", label: "Last active", className: "text-muted-foreground text-xs", cell: (u) => (u.lastSeenAt ? fmtDate(u.lastSeenAt) : "never") },
        {
          key: "actions",
          label: "",
          actions: true,
          className: "text-right",
          cell: (u) =>
            u.role !== "superadmin" ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={setDeactivated.isPending}
                onClick={() => {
                  if (u.deactivated || window.confirm(`Deactivate ${u.email}? They are refused on their next request.`)) {
                    setDeactivated.mutate({ id: u.id, deactivated: !u.deactivated });
                  }
                }}
              >
                {u.deactivated ? "Reactivate" : "Deactivate"}
              </Button>
            ) : null,
        },
      ]}
      footer={
        <>
          {setRole.isError ? <p className="text-destructive px-3 py-2 text-xs">{(setRole.error as Error).message}</p> : null}
          {setDeactivated.isError ? <p className="text-destructive px-3 py-2 text-xs">{(setDeactivated.error as Error).message}</p> : null}
        </>
      }
    />
  );
}

function AllBriefs() {
  const briefs = useAllBriefs();
  const users = useUsage();
  if (briefs.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (briefs.isError) return <p className="text-destructive text-sm">Could not load briefs.</p>;
  const who = new Map((users.data ?? []).map((u) => [u.id, u.name ?? u.email]));
  return (
    <ul className="divide-border bg-card divide-y rounded-lg border">
      {briefs.data.map((b) => (
        <li key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
          <div className="min-w-0 flex-1">
            <Link to={`/briefs/${b.id}`} className="block truncate font-medium hover:underline">
              {b.title}
            </Link>
            <div className="text-muted-foreground truncate text-xs">{who.get(b.ownerId) ?? b.ownerId}</div>
          </div>
          <div className="text-muted-foreground flex shrink-0 items-center gap-3 text-xs">
            <span>{b.submissionCount === 0 ? "draft" : `v${b.submissionCount}${b.editedSinceSubmit ? " · edited since" : ""}`}</span>
            <span>
              {b.clear} of {b.core} clear
            </span>
            {b.adminLabel ? <span className="bg-accent text-accent-foreground rounded-full px-2 py-0.5">{b.adminLabel}</span> : null}
            {b.archivedAt ? <span>archived</span> : null}
            <span>{fmtDate(b.updatedAt)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Canary({ result }: { result: CanaryResult | null | undefined }) {
  if (!result) return null;
  return (
    <ul className="mt-2 space-y-0.5 text-xs">
      {result.checks.map((c) => (
        <li key={c.name} className={c.ok ? "text-success" : "text-destructive"}>
          {c.ok ? "✓" : "✗"} {c.name} · {ms(c.ms)} · <span className="text-muted-foreground">{c.detail}</span>
        </li>
      ))}
    </ul>
  );
}

function ModelRow({ role, model, fallbackModel, think, catalog, enabled }: { role: string; model: string; fallbackModel: string | null; think: boolean; catalog: string[]; enabled: boolean }) {
  const { setModel, canary } = useAiActions();
  const [next, setNext] = useState(model);
  const [nextThink, setNextThink] = useState(think);
  const [force, setForce] = useState(false);
  const changed = next !== model || nextThink !== think;
  const result = setModel.data?.canary ?? canary.data ?? null;
  return (
    <div className="border-border rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-20 text-sm font-medium uppercase">{role}</span>
        <input list={`models-${role}`} value={next} onChange={(e) => setNext(e.target.value)} className="border-input bg-card h-8 min-w-56 flex-1 rounded border px-2 text-sm" />
        <datalist id={`models-${role}`}>
          {catalog.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={nextThink} onChange={(e) => setNextThink(e.target.checked)} /> thinking
        </label>
        <Button size="sm" variant="outline" disabled={!enabled || canary.isPending} onClick={() => canary.mutate({ model: next, role, think: nextThink })}>
          {canary.isPending ? "Checking…" : "Test"}
        </Button>
        <Button size="sm" disabled={!changed || setModel.isPending} onClick={() => setModel.mutate({ role, model: next, think: nextThink, force })}>
          {setModel.isPending ? "Saving…" : "Save"}
        </Button>
        <label className="text-muted-foreground flex items-center gap-1 text-xs" title="Skip the checks. Only when the provider is down and you know it.">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> force
        </label>
      </div>
      <div className="text-muted-foreground mt-1 text-xs">
        Live: <span className="text-foreground">{model}</span> · fallback: {fallbackModel ?? "none"} · thinking {think ? "on" : "off"}
      </div>
      {setModel.isError ? <p className="text-destructive mt-1 text-xs">{(setModel.error as Error).message}</p> : null}
      {setModel.data && !setModel.data.ok ? <p className="text-destructive mt-1 text-xs">{setModel.data.message}</p> : null}
      <Canary result={result} />
    </div>
  );
}

function Ai() {
  const [days, setDays] = useState(7);
  const ai = useAiTab(days);
  const catalog = useCatalog();
  const { setLimit } = useAiActions();
  if (ai.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (ai.isError) return <p className="text-destructive text-sm">Could not load the AI tab.</p>;
  const d = ai.data;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className={cn("rounded-full px-2.5 py-1 text-xs", d.enabled ? "bg-accent text-accent-foreground" : "bg-destructive/10 text-destructive")}>
          AI {d.enabled ? "enabled" : "disabled"}
        </span>
        <span>
          Spend today: <span className="font-medium">{money(d.spendTodayUsd)}</span> (estimate)
        </span>
        <span className="text-muted-foreground">
          Gate: {d.gate.running.interactive}+{d.gate.running.background} running, {d.gate.queued.interactive}+{d.gate.queued.background} queued
        </span>
        {d.circuits.filter((c) => c.openUntil > Date.now()).map((c) => (
          <span key={c.model} className="bg-destructive/10 text-destructive rounded-full px-2.5 py-1 text-xs">
            circuit open: {c.model}
          </span>
        ))}
        <span className="ml-auto flex items-center gap-1 text-xs">
          Window
          {[1, 7, 30, 90].map((n) => (
            <button key={n} onClick={() => setDays(n)} className={cn("rounded px-2 py-1", days === n ? "bg-accent text-accent-foreground" : "text-muted-foreground")}>
              {n}d
            </button>
          ))}
        </span>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Models per role</h2>
        <p className="text-muted-foreground mb-2 text-xs">
          Saving a different model runs its checks first; the model it replaces becomes the fallback. Users never see a model name.
        </p>
        <div className="space-y-2">
          {d.models.map((m) => (
            <ModelRow key={m.role} {...m} catalog={catalog.data ?? []} enabled={d.enabled} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Success rate, last {d.windowDays} days</h2>
        <ResponsiveTable
          rows={d.metrics}
          rowKey={(m) => `${m.model}-${m.role}`}
          rowClassName={() => "tabular-nums"}
          empty="No calls in this window."
          columns={[
            { key: "model", label: "Model", primary: true, className: "font-medium", cell: (m) => m.model },
            { key: "role", label: "Role", cell: (m) => m.role },
            { key: "calls", label: "Calls", align: "right", cell: (m) => m.calls },
            { key: "success", label: "Success", align: "right", cell: (m) => <span className={cn(m.successRate < 0.9 && "text-destructive")}>{pct(m.successRate)}</span> },
            { key: "tool", label: "Tool valid", align: "right", cell: (m) => pct(m.toolValidity) },
            { key: "json", label: "JSON valid", align: "right", cell: (m) => pct(m.jsonValidity) },
            { key: "first", label: "First token p50", align: "right", cell: (m) => ms(m.firstTokenP50Ms) },
            {
              key: "latency",
              label: "p50 / p95",
              align: "right",
              cell: (m) => (
                <>
                  {ms(m.p50Ms)} / {ms(m.p95Ms)}
                </>
              ),
            },
            {
              key: "tokens",
              label: "Tokens in / out",
              align: "right",
              cell: (m) => (
                <>
                  {m.inputTokens.toLocaleString()} / {m.outputTokens.toLocaleString()}
                </>
              ),
            },
            { key: "cost", label: "Est. cost", align: "right", cell: (m) => money(m.costUsd) },
          ]}
        />
        {d.acceptance.length > 0 ? (
          <p className="text-muted-foreground mt-2 text-xs">
            AI writes kept or edited rather than undone:{" "}
            {d.acceptance.map((a) => `${a.model} ${pct(a.rate)} of ${a.writes}`).join(" · ")}
          </p>
        ) : null}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Caps</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {d.limits.map((l) => (
            <LimitRow key={l.key} limitKey={l.key} value={l.value} fallback={l.default} onSave={(value) => setLimit.mutate({ key: l.key, value })} busy={setLimit.isPending} />
          ))}
        </div>
      </section>
    </div>
  );
}

const LIMIT_LABELS: Record<string, string> = {
  turns_per_user_per_day: "AI replies per person per day",
  assessments_per_brief_per_hour: "Section checks per brief per hour",
  concurrency: "Concurrent requests (the Ollama plan's limit)",
  cost_alert_usd: "Daily cost alert, USD",
};

function LimitRow({ limitKey, value, fallback, onSave, busy }: { limitKey: string; value: number; fallback: number; onSave: (v: number) => void; busy: boolean }) {
  const [v, setV] = useState(String(value));
  return (
    <div className="border-border flex items-center gap-2 rounded-lg border p-3">
      <div className="flex-1">
        <Label>{LIMIT_LABELS[limitKey] ?? limitKey}</Label>
        <div className="text-muted-foreground text-[11px]">default {fallback}</div>
      </div>
      <div className="w-24 shrink-0">
        <Input type="number" min={0} value={v} onChange={(e) => setV(e.target.value)} className="text-right" />
      </div>
      <Button size="sm" variant="outline" disabled={busy || Number(v) === value} onClick={() => onSave(Number(v))}>
        Save
      </Button>
    </div>
  );
}

function KnownData() {
  const items = useKnownData();
  const { create, update, remove } = useKnownDataActions();
  const [form, setForm] = useState({ appSlug: "", appName: "", holds: "" });
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        One line per app about the kind of data it holds. The interviewer may mention these as things a person may already have. Descriptions only: the AI has no access to any of it. Add a line when a new app appears on Home.
      </p>
      <form
        className="bg-card grid gap-2 rounded-lg border p-3 sm:grid-cols-[1fr_1fr_2fr_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(form, { onSuccess: () => setForm({ appSlug: "", appName: "", holds: "" }) });
        }}
      >
        <Input placeholder="slug (as on Home)" value={form.appSlug} onChange={(e) => setForm({ ...form, appSlug: e.target.value })} required />
        <Input placeholder="App name" value={form.appName} onChange={(e) => setForm({ ...form, appName: e.target.value })} required />
        <Input placeholder="What it holds, in one line" value={form.holds} onChange={(e) => setForm({ ...form, holds: e.target.value })} required />
        <Button type="submit" disabled={create.isPending}>
          Add
        </Button>
      </form>
      {create.isError ? <p className="text-destructive text-xs">{(create.error as Error).message}</p> : null}
      {items.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <ul className="divide-border bg-card divide-y rounded-lg border">
          {(items.data ?? []).map((k) => (
            <li key={k.id} className={cn("flex flex-wrap items-start gap-3 px-4 py-3 text-sm", !k.enabled && "opacity-60")}>
              <div className="min-w-0 flex-1">
                <div className="font-medium">
                  {k.appName} <span className="text-muted-foreground text-xs">({k.appSlug})</span>
                </div>
                <Textarea
                  defaultValue={k.holds}
                  className="mt-1 min-h-[48px]"
                  onBlur={(e) => e.target.value.trim() !== k.holds && update.mutate({ id: k.id, holds: e.target.value.trim() })}
                />
              </div>
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="sm" onClick={() => update.mutate({ id: k.id, enabled: !k.enabled })}>
                  {k.enabled ? "Disable" : "Enable"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm(`Remove ${k.appName}?`)) remove.mutate(k.id);
                  }}
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
          {items.data?.length === 0 ? <li className="text-muted-foreground px-4 py-3 text-sm">Nothing yet.</li> : null}
        </ul>
      )}
    </div>
  );
}

const VERDICT: Record<"ok" | "warn" | "fail", { label: string; cls: string }> = {
  ok: { label: "✓ All good", cls: "bg-accent text-accent-foreground" },
  warn: { label: "⚠ To check", cls: "bg-warning/10 text-warning" },
  fail: { label: "✗ Failing", cls: "bg-destructive/10 text-destructive" },
};

function Digest() {
  const digest = useDigest();
  const { preview, send } = useDigestActions();
  if (digest.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (digest.isError) return <p className="text-destructive text-sm">Could not load the digest.</p>;
  const { settings, runs } = digest.data;
  return (
    <div className="space-y-6">
      <section className="space-y-2 text-sm">
        <p>
          The daily digest goes to <span className="font-medium">{settings.recipients.join(", ") || "nobody"}</span>.{" "}
          {settings.schedule}, up to {settings.maxAttempts} attempts. It reports who used the app, briefs started and submitted, AI tokens and cost, and
          whether the app and each AI model are working. It is sent on quiet days too, so a missing email means something is down.
        </p>
        {settings.problems.length > 0 ? (
          <p className="text-destructive">Not sending: {settings.problems.join("; ")}</p>
        ) : null}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="secondary" size="sm" disabled={preview.isPending} onClick={() => preview.mutate(false)}>
            {preview.isPending ? "Building…" : "Preview"}
          </Button>
          <Button variant="secondary" size="sm" disabled={preview.isPending} onClick={() => preview.mutate(true)}>
            Preview with AI probe
          </Button>
          <Button
            size="sm"
            disabled={send.isPending || !settings.enabled}
            onClick={() => {
              if (window.confirm(`Send the digest for the last 24 hours to ${settings.recipients.join(", ")} now? The 08:00 digest still goes.`)) send.mutate();
            }}
          >
            {send.isPending ? "Sending…" : "Send now"}
          </Button>
        </div>
        {send.isSuccess ? <p className="text-muted-foreground text-xs">Sent: {send.data.subject}</p> : null}
        {send.isError ? <p className="text-destructive text-xs">{send.error instanceof Error ? send.error.message : "Not sent."}</p> : null}
        {preview.isError ? <p className="text-destructive text-xs">Could not build the preview.</p> : null}
      </section>

      {preview.data ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Preview: {preview.data.subject}</h2>
          {/* Sandboxed: the preview is our own escaped HTML, and it still gets no scripts and no same-origin access. */}
          <iframe title="Digest preview" sandbox="" srcDoc={preview.data.html} className="h-[640px] w-full rounded-lg border bg-white" />
        </section>
      ) : null}

      <section>
        <h2 className="mb-2 text-sm font-semibold">Last two weeks</h2>
        <ResponsiveTable
          rows={runs}
          rowKey={(r) => r.day}
          rowClassName={() => "align-top tabular-nums"}
          empty="No digest has run yet. The first goes at 08:00 SGT."
          columns={[
            { key: "day", label: "Day", primary: true, className: "whitespace-nowrap", cell: (r) => r.day },
            {
              key: "sent",
              label: "Sent",
              className: "text-xs",
              cell: (r) =>
                r.status === "sent" ? (
                  <span>
                    ✓ {r.finishedAt ? fmtDate(r.finishedAt) : ""}
                    {r.attempts > 1 ? ` (attempt ${r.attempts})` : ""}
                  </span>
                ) : r.status === "running" ? (
                  <span>Sending… (attempt {r.attempts})</span>
                ) : (
                  <span className="text-destructive">
                    ✗ Failed after {r.attempts} attempt{r.attempts === 1 ? "" : "s"}: {r.error}
                  </span>
                ),
            },
            {
              key: "health",
              label: "Health",
              className: "text-xs",
              cell: (r) =>
                r.verdict ? (
                  <span className={cn("rounded-full px-2 py-0.5 whitespace-nowrap", VERDICT[r.verdict].cls)}>
                    {VERDICT[r.verdict].label}
                    {r.headline && r.verdict !== "ok" ? ` (${r.headline.checks.fail + r.headline.checks.warn})` : ""}
                  </span>
                ) : (
                  "—"
                ),
            },
            { key: "people", label: "People", align: "right", cell: (r) => r.headline?.people ?? "—" },
            { key: "submitted", label: "Submitted", align: "right", cell: (r) => r.headline?.submissions ?? "—" },
            { key: "tokens", label: "Tokens", align: "right", cell: (r) => (r.headline ? r.headline.tokens.toLocaleString() : "—") },
            { key: "cost", label: "Est. cost", align: "right", cell: (r) => (r.headline ? money(r.headline.costUsd) : "—") },
          ]}
        />
      </section>
    </div>
  );
}

function Audit() {
  const audit = useAudit();
  const users = useUsage();
  const who = new Map((users.data ?? []).map((u) => [u.id, u.name ?? u.email]));
  if (audit.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (audit.isError) return <p className="text-destructive text-sm">Could not load the audit log.</p>;
  return (
    <ResponsiveTable
      rows={audit.data}
      rowKey={(e) => e.id}
      rowClassName={() => "align-top"}
      columns={[
        { key: "when", label: "When", className: "text-muted-foreground text-xs whitespace-nowrap", cell: (e) => fmtDate(e.at) },
        { key: "who", label: "Who", className: "text-xs", cell: (e) => (e.actorId ? (who.get(e.actorId) ?? e.actorId.slice(0, 8)) : "system") },
        { key: "action", label: "Action", primary: true, className: "text-xs font-medium", cell: (e) => e.action },
        {
          key: "target",
          label: "Target",
          className: "text-muted-foreground text-xs",
          cell: (e) =>
            e.targetType === "brief" && e.targetId ? (
              <Link to={`/briefs/${e.targetId}`} className="hover:underline">
                brief {e.targetId.slice(0, 8)}
              </Link>
            ) : e.targetType === "user" && e.targetId ? (
              (who.get(e.targetId) ?? e.targetId.slice(0, 8))
            ) : (
              [e.targetType, e.targetId?.slice(0, 12)].filter(Boolean).join(" ")
            ),
        },
        { key: "detail", label: "Detail", className: "text-muted-foreground font-mono text-[11px]", cell: (e) => (e.detail ? <span className="font-mono text-[11px]">{JSON.stringify(e.detail)}</span> : "") },
      ]}
    />
  );
}
